// src/blogs/FilterSidebar.jsx
// Shared filter panel: tag toggles + a month-grouped, click-through date list.
// `extra` is an optional slot rendered below the date list — used only by
// MovieBlog, for its poster grid. Floats detached from the screen edges with
// margin when open (default); collapses flush into the right edge when not,
// via a CSS transition rather than mounting/unmounting the content.
//
// `glass` (Thoughts only, so far) hands the panel's surface over to the 3D
// backdrop: it registers with glassPanels.js so a glass slab is drawn behind
// it, and FilterSidebar.css clears its own background to let that show.
import React, { useMemo, useState } from "react";

import { glassPanelRef } from "./glassPanels.js";
import "./FilterSidebar.css";

function monthLabel(iso) {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

function dayLabel(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function FilterSidebar({
  posts,
  activeTags,
  onToggleTag,
  onJumpDate,
  open,
  onToggleOpen,
  extra,
  glass = false,
}) {
  const [datesOpen, setDatesOpen] = useState(true);

  const tags = useMemo(() => {
    const set = new Set();
    posts.forEach((post) => post.tags.forEach((tag) => set.add(tag)));
    return Array.from(set).sort();
  }, [posts]);

  const dateGroups = useMemo(() => {
    const groups = new Map();
    posts.forEach((post) => {
      const key = monthLabel(post.date);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(post);
    });
    return Array.from(groups.entries());
  }, [posts]);

  return (
    <aside
      className={`filter-sidebar ${
        open ? "filter-sidebar--open" : "filter-sidebar--collapsed"
      }${glass ? " filter-sidebar--glass" : ""}`}
      ref={glass ? glassPanelRef : undefined}
      // Keep in step with .filter-sidebar's border-radius in FilterSidebar.css.
      data-glass-radius={glass ? 22 : undefined}
    >
      <button
        type="button"
        className="filter-sidebar__handle"
        onClick={onToggleOpen}
        aria-expanded={open}
        aria-label={open ? "Collapse filters" : "Expand filters"}
      >
        <span className="filter-sidebar__handle-label">Filters</span>
        <span className="filter-sidebar__handle-chevron">{open ? "›" : "‹"}</span>
      </button>

      <div className="filter-sidebar__panel-content" aria-hidden={!open}>
        <section className="filter-sidebar__section">
          <h3 className="filter-sidebar__heading">Tags</h3>
          <div className="filter-sidebar__tags">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`filter-sidebar__tag ${
                  activeTags.has(tag) ? "filter-sidebar__tag--active" : ""
                }`}
                onClick={() => onToggleTag(tag)}
                tabIndex={open ? 0 : -1}
              >
                #{tag}
              </button>
            ))}
          </div>
        </section>

        <section className="filter-sidebar__section">
          <button
            type="button"
            className="filter-sidebar__heading filter-sidebar__heading--button"
            onClick={() => setDatesOpen((v) => !v)}
            aria-expanded={datesOpen}
            tabIndex={open ? 0 : -1}
          >
            Dates {datesOpen ? "▾" : "▸"}
          </button>

          {datesOpen && (
            <div className="filter-sidebar__dates">
              {dateGroups.map(([month, monthPosts]) => (
                <div key={month} className="filter-sidebar__month">
                  <div className="filter-sidebar__month-label">{month}</div>
                  {monthPosts.map((post) => (
                    <button
                      key={post.id}
                      type="button"
                      className="filter-sidebar__date-row"
                      onClick={() => onJumpDate(post.id)}
                      title={post.title}
                      tabIndex={open ? 0 : -1}
                    >
                      <span className="filter-sidebar__date-day">{dayLabel(post.date)}</span>
                      <span className="filter-sidebar__date-title">{post.title}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>

        {extra && <section className="filter-sidebar__section">{extra}</section>}
      </div>
    </aside>
  );
}
